import { Button, Row, Col, Radio, Table, Tag, message, Input, Select, DatePicker } from 'antd';
import { calculateExactAge, getUserDetails, makePostCall, makeGetCall, parseMlcInfo } from '../../utils/helper';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import { MedicineBoxOutlined, FlagOutlined, ProfileOutlined, HeartOutlined, UserOutlined } from '@ant-design/icons';
import './order-info.scss';
import { FieldLabels, getPacsStatusLabel, getPatientIdLabel } from '../../constants/fieldLabels';
// Shared across every table's Status column — never fork a local copy.
import { getStatusBadgeStyle } from '../../utils/statusBadgeStyle';
import {
  PATIENT_TYPE_OPTIONS,
  IMPAIRMENT_OPTIONS,
  YES_NO_NA_OPTIONS,
  MEDICAL_QUESTIONS,
} from './constants';

const REF_DATE_FMT = 'YYYY-MM-DD';

// Serialize the tracked order-info fields for a cheap changed/unchanged compare.
// Pure + module-scoped so the initial baseline (empty input) can seed the ref
// before the prefill fetch resolves — otherwise the Save button lights up on open.
const buildSnapshot = (s: any) => JSON.stringify({
  vip: !!s.vip,
  insurance: !!s.insurance,
  patientType: s.patientType || '',
  impairment: s.impairment || '',
  refDate: s.refDate ? s.refDate.format(REF_DATE_FMT) : '',
  orderDesc: s.orderDesc || '',
  invoiceNotes: s.invoiceNotes || '',
  refDoc: s.refDoc || '',
  med: MEDICAL_QUESTIONS.reduce((acc, q) => {
    acc[q.key] = s.med?.[q.key] || '';
    return acc;
  }, {} as Record<string, string>),
  highRiskMsg: s.highRiskMsg || '',
});

/**
 * `readOnly` opens the popup as a pure viewer: every editable control is
 * disabled and UPDATE INFO can never fire. Used by the Orders list for roles
 * that may look at an order but not change it (radiologists / HODs). Defaults
 * to false so the PACS and Worklist callers keep their existing edit behaviour.
 */
const OrderInfoPopup = ({ data, closeModal, refreshTable, readOnly = false }) => {

  const patInfo = data?.pacs_order?.patient;
  const ordInfo = data?.pacs_order;

  const [scans, setScans] = useState({ data: [], loading: false });
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [physicianNotes, setPhysicianNotes] = useState('');
  const [priority, setPriority] = useState('NORMAL');

  // MLC state
  const [isMlc, setIsMlc] = useState<boolean | null>(null);
  const [showMlcExtra, setShowMlcExtra] = useState(false);
  const [mlcNumber, setMlcNumber] = useState('');
  const [mlcCategory, setMlcCategory] = useState<string | undefined>(undefined);
  const [mlcStation, setMlcStation] = useState('');
  const [mlcFir, setMlcFir] = useState('');
  const [mlcRemarks, setMlcRemarks] = useState('');
  const [mlcSaving, setMlcSaving] = useState(false);

  // ── T008: Order Information (extra) fields ──────────────────────────────
  const [vip, setVip] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const [patientType, setPatientType] = useState<string | undefined>(undefined); // po_pat_class
  const [impairment, setImpairment] = useState<string | undefined>(undefined);
  const [refDate, setRefDate] = useState<dayjs.Dayjs | null>(null);
  const [orderDesc, setOrderDesc] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [refDoc, setRefDoc] = useState(''); // po_ref_doc

  // ── T008: Medical Information ───────────────────────────────────────────
  const [med, setMed] = useState<Record<string, string | undefined>>({});
  const [highRiskMsg, setHighRiskMsg] = useState('');

  const [saving, setSaving] = useState(false);

  // Snapshot of the loaded order-info fields, for change detection. Seeded with
  // the empty baseline so the Save button stays disabled until something changes.
  const initialSnapshotRef = useRef<string>(buildSnapshot({}));

  // ── Item Information lookup (items_master via /items) ────────────────────
  const [allItems, setAllItems] = useState<any[]>([]);
  const [billingCodeSearch, setBillingCodeSearch] = useState('');
  const [descSearch, setDescSearch] = useState('');

  useEffect(() => {
    if (ordInfo?.pacs_ord_id) {
      fetchAllScans(ordInfo.pacs_ord_id);
      fetchOrderInfo(ordInfo.pacs_ord_id);
    }

    if (ordInfo?.po_priority) {
      setPriority(ordInfo.po_priority);
    }

    if (ordInfo) {
      const isCurrentlyMlc = ordInfo.po_is_mlc === true;
      setIsMlc(isCurrentlyMlc ? true : false);
      if (isCurrentlyMlc) {
        const info = parseMlcInfo(ordInfo.po_mlc_info);
        setMlcNumber(ordInfo.po_mlc_number || '');
        setMlcCategory(info.category || undefined);
        setMlcStation(info.station || '');
        setMlcFir(info.fir || '');
        setMlcRemarks(info.remarks || '');
        if (ordInfo.po_mlc_number || info.category || info.station || info.fir || info.remarks) {
          setShowMlcExtra(true);
        }
      }
    }
  }, [ordInfo]);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchAllScans = async (pacs_ord_id) => {
    // fetch scans from API
    await makePostCall('/get-all-scans', { pacs_ord_id: pacs_ord_id }).then((res) => {
      setScans({ data: res.data.data, loading: false });
    }).catch((err) => {
      setScans({ data: [], loading: false });
    });
  }

  // Lazy prefill of the editable order-info fields (po_extra_info lives outside
  // the list queries, so we fetch it per-order when the modal opens).
  const fetchOrderInfo = async (pacs_ord_id) => {
    try {
      const res = await makePostCall('/get-order-info', { pacs_ord_id });
      const order = res?.data?.data;
      if (!order) return;

      const extra = order.po_extra_info || {};
      const medInfo = extra.med || {};

      const nextVip = extra.vip === true;
      const nextInsurance = extra.insurance === true;
      const nextPatientType = order.po_pat_class || undefined;
      const nextImpairment = extra.impairment || undefined;
      const nextRefDate = extra.ref_date ? dayjs(extra.ref_date) : null;
      const nextOrderDesc = extra.order_desc || '';
      const nextInvoiceNotes = extra.invoice_notes || '';
      const nextRefDoc = order.po_ref_doc || '';
      const nextHighRisk = extra.high_risk_msg || '';

      setVip(nextVip);
      setInsurance(nextInsurance);
      setPatientType(nextPatientType);
      setImpairment(nextImpairment);
      setRefDate(nextRefDate);
      setOrderDesc(nextOrderDesc);
      setInvoiceNotes(nextInvoiceNotes);
      setRefDoc(nextRefDoc);
      setMed(medInfo);
      setHighRiskMsg(nextHighRisk);

      initialSnapshotRef.current = buildSnapshot({
        vip: nextVip,
        insurance: nextInsurance,
        patientType: nextPatientType,
        impairment: nextImpairment,
        refDate: nextRefDate,
        orderDesc: nextOrderDesc,
        invoiceNotes: nextInvoiceNotes,
        refDoc: nextRefDoc,
        med: medInfo,
        highRiskMsg: nextHighRisk,
      });
    } catch (e) {
      console.error('Error fetching order info', e);
    }
  };

  const fetchItems = async () => {
    try {
      const res = await makeGetCall('/items');
      if (res?.data?.success) {
        setAllItems(res.data.data || []);
      }
    } catch (e) {
      console.error('Error fetching items', e);
    }
  };

  const currentSnapshot = useMemo(
    () => buildSnapshot({
      vip, insurance, patientType, impairment, refDate,
      orderDesc, invoiceNotes, refDoc, med, highRiskMsg,
    }),
    [vip, insurance, patientType, impairment, refDate, orderDesc, invoiceNotes, refDoc, med, highRiskMsg]
  );

  const orderInfoChanged = currentSnapshot !== initialSnapshotRef.current;

  const scanColumns = [
    {
      title: FieldLabels.MODALITY,
      dataIndex: 'po_modality',
    },
    {
      title: FieldLabels.ITEM_CODE,
      dataIndex: 'po_diag_no'
    },
    {
      title: FieldLabels.DESCRIPTION,
      dataIndex: 'po_diag_desc',
    },
    {
      title: FieldLabels.ACCESSION_NUMBER_SHORTEST,
      dataIndex: 'po_acc_no',
    },
    {
      title: FieldLabels.STATUS,
      // The same badge every other Status column renders (utils/statusBadgeStyle);
      // this order-history table used to show the status as bare text.
      render: (itm, rec) => {
        const status = rec?.order_workflow?.ow_pacs_status;
        if (!status) return <span>-</span>;
        return <Tag style={getStatusBadgeStyle(status)}>{getPacsStatusLabel(status)}</Tag>;
      }
    }
  ];

  const handlePriorityChange = (e) => {
    setPriority(e.target.value);
  }

  const setMedValue = (key, value) => {
    setMed(prev => ({ ...prev, [key]: value }));
  };

  // Client-side lookup against items_master. Matches on HIS billing code and/or
  // description; both empty => show nothing (avoid dumping the whole master).
  const matchedItems = useMemo(() => {
    const code = billingCodeSearch.trim().toLowerCase();
    const desc = descSearch.trim().toLowerCase();
    if (!code && !desc) return [];
    return allItems.filter(item => {
      const codeHit = !code
        || (item.his_billing_code || '').toLowerCase().includes(code)
        || (item.item_code || '').toLowerCase().includes(code);
      const descHit = !desc || (item.item_name || '').toLowerCase().includes(desc);
      return codeHit && descHit;
    }).slice(0, 8);
  }, [billingCodeSearch, descSearch, allItems]);

  const mlcChanged = useMemo(() => {
    const prevIsMlc = ordInfo?.po_is_mlc === true;
    if (isMlc === null) return false;
    if (isMlc !== prevIsMlc) return true;
    if (!isMlc) return false;
    const info = parseMlcInfo(ordInfo?.po_mlc_info);
    return (
      mlcNumber !== (ordInfo?.po_mlc_number || '') ||
      (mlcCategory || undefined) !== (info.category || undefined) ||
      mlcStation !== (info.station || '') ||
      mlcFir !== (info.fir || '') ||
      mlcRemarks !== (info.remarks || '')
    );
  }, [isMlc, mlcNumber, mlcCategory, mlcStation, mlcFir, mlcRemarks, ordInfo]);

  const checkButtonEnable = useMemo(() => {
    const prevPriority = ordInfo.po_priority || 'NORMAL';
    if (!!clinicalNotes && clinicalNotes.length > 0 ||
      !!physicianNotes && physicianNotes.length > 0 ||
      (priority !== prevPriority) ||
      mlcChanged ||
      orderInfoChanged
    ) {
      return false
    } else {
      return true
    }
  }, [priority, clinicalNotes, physicianNotes, mlcChanged, orderInfoChanged]);

  const handleSubmit = async () => {
    if (readOnly) return;
    const prevPriority = ordInfo.po_priority || 'NORMAL';
    const priorityChanged = priority !== prevPriority;

    let success = false;

    try {
      setSaving(true);

      if (priorityChanged) {
        const payload = {
          pacs_ord_id: ordInfo.pacs_ord_id,
          priority: priority,
        };
        await makePostCall('/update-his-order-priority', payload);
        message.success("Priority updated successfully");
        success = true;
      }

      if (!!clinicalNotes || !!physicianNotes) {
        const formData = new FormData();
        const orderIds = scans?.data?.map(itm => itm.pacs_ord_id);
        formData.append('order_ids', orderIds.join(','));
        formData.append('type', 'notes');
        formData.append('notes', clinicalNotes);
        formData.append('user_id', getUserDetails()?.username);

        await makePostCall('/upload-notes', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        message.success("Notes added successfully");
        success = true;
      }

      if (mlcChanged) {
        const shouldMark = isMlc === true;
        const payload = {
          order_id: ordInfo.pacs_ord_id,
          user_id: getUserDetails()?.username,
          markMlc: shouldMark,
          mlc_number: shouldMark ? (mlcNumber.trim() || null) : null,
          mlc_info: shouldMark
            ? { category: mlcCategory || null, station: mlcStation.trim() || null, fir: mlcFir.trim() || null, remarks: mlcRemarks.trim() || null }
            : null,
        };
        await makePostCall('/mark-mlc', payload);
        message.success(shouldMark ? 'Marked as MLC' : 'MLC marking removed');
        success = true;
      }

      if (orderInfoChanged) {
        const po_extra_info = {
          vip,
          insurance,
          impairment: impairment || null,
          order_desc: orderDesc || null,
          invoice_notes: invoiceNotes || null,
          ref_date: refDate ? refDate.format(REF_DATE_FMT) : null,
          high_risk_msg: highRiskMsg || null,
          med: MEDICAL_QUESTIONS.reduce((acc, q) => {
            acc[q.key] = med?.[q.key] || null;
            return acc;
          }, {} as Record<string, any>),
        };
        await makePostCall('/update-order-info', {
          pacs_ord_id: ordInfo.pacs_ord_id,
          po_extra_info,
          po_pat_class: patientType || null,
          po_ref_doc: refDoc || null,
        });
        message.success('Order info updated successfully');
        success = true;
      }

      if (success) {
        refreshTable?.();
        closeModal?.();
      }
    } catch (e) {
      message.error('Something went wrong');
      console.error("Error during update", e);
    } finally {
      setSaving(false);
      setMlcSaving(false);
    }
  };


  const patientFields = [
    { label: FieldLabels.PATIENT_NAME, value: patInfo?.pat_name },
    { label: getPatientIdLabel(), value: patInfo?.pat_pin },
    { label: FieldLabels.PATIENT_AGE, value: calculateExactAge(patInfo?.pat_dob) },
    { label: FieldLabels.PATIENT_REPORT_SEX, value: patInfo?.pat_sex },
    { label: 'Order No', value: ordInfo?.po_his_ord_no },
    { label: 'Site', value: data?.pacs_order?.po_site || data?.pacs_order?.po_center_name || '' },
  ];

  return (
    <div className="order-info-container">
      <div className="oi-header">PATIENT &amp; ORDER INFO</div>

      <div className="patient-card">
        {patientFields.map(field => (
          <div className="pc-field" key={field.label}>
            <span className="pc-label">{field.label}</span>
            <span className="pc-value">{field.value || '—'}</span>
          </div>
        ))}
      </div>

      {/* ── Order Information ─────────────────────────────────────────────── */}
      <div className="oi-panel">
        <div className="oi-panel-head">
          <ProfileOutlined /> Order Information
        </div>
        <div className="oi-panel-body">
          <div className="oi-grid">
            <div className="oi-grid-field">
              <span className="oi-grid-label">VIP</span>
              <Radio.Group value={vip} onChange={e => setVip(e.target.value)} disabled={readOnly}>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </div>

            <div className="oi-grid-field">
              <span className="oi-grid-label">Insurance</span>
              <Radio.Group value={insurance} onChange={e => setInsurance(e.target.value)} disabled={readOnly}>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </div>

            <div className="oi-grid-field">
              <span className="oi-grid-label">Patient Type</span>
              <Select
                allowClear
                disabled={readOnly}
                placeholder="Select"
                value={patientType}
                onChange={val => setPatientType(val)}
                options={PATIENT_TYPE_OPTIONS}
                style={{ width: '100%' }}
              />
            </div>

            <div className="oi-grid-field">
              <span className="oi-grid-label">Impairment</span>
              <Select
                allowClear
                showSearch
                disabled={readOnly}
                placeholder="Select"
                value={impairment}
                onChange={val => setImpairment(val)}
                options={IMPAIRMENT_OPTIONS}
                style={{ width: '100%' }}
              />
            </div>

            <div className="oi-grid-field">
              <span className="oi-grid-label">Ref. Date</span>
              <DatePicker
                value={refDate}
                onChange={val => setRefDate(val)}
                disabled={readOnly}
                format="DD-MM-YYYY"
                style={{ width: '100%' }}
              />
            </div>

            <div className="oi-grid-field">
              <span className="oi-grid-label">Order Desc.</span>
              <Input
                value={orderDesc}
                onChange={e => setOrderDesc(e.target.value)}
                disabled={readOnly}
                allowClear
              />
            </div>

            <div className="oi-grid-field oi-grid-full">
              <span className="oi-grid-label">Invoice Notes</span>
              <TextArea
                rows={2}
                value={invoiceNotes}
                onChange={e => setInvoiceNotes(e.target.value)}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="oi-field oi-field-top">
            <span className="oi-field-label">Priority</span>
            <Radio.Group onChange={handlePriorityChange} value={priority} disabled={readOnly}>
              <Radio value="NORMAL">Normal</Radio>
              <Radio value="EMERGENCY">Emergency</Radio>
              <Radio value="ASAP">ASAP</Radio>
            </Radio.Group>
          </div>

          <div className="oi-field">
            <span className="oi-field-label">
              <FlagOutlined /> MLC
            </span>
            <Radio.Group
              value={isMlc}
              disabled={readOnly}
              onChange={e => {
                setIsMlc(e.target.value);
                if (!e.target.value) setShowMlcExtra(false);
              }}
            >
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
            {isMlc && (
              <span
                className="oi-link"
                onClick={() => setShowMlcExtra(prev => !prev)}
              >
                {showMlcExtra ? 'Hide extra information' : 'Add extra information'}
              </span>
            )}
          </div>

          {isMlc && showMlcExtra && (
            <div className="oi-mlc-extra">
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <div className="oi-mlc-label">MLC Number</div>
                  <Input
                    placeholder="Optional — register number"
                    value={mlcNumber}
                    onChange={e => setMlcNumber(e.target.value)}
                    disabled={readOnly}
                    allowClear
                  />
                </Col>
                <Col span={12}>
                  <div className="oi-mlc-label">Category / Type</div>
                  <Select
                    allowClear
                    showSearch
                    placeholder="Optional"
                    value={mlcCategory}
                    onChange={val => setMlcCategory(val)}
                    disabled={readOnly}
                    style={{ width: '100%' }}
                    options={[
                      { value: 'RTA', label: 'Road Traffic Accident (RTA)' },
                      { value: 'Assault', label: 'Assault' },
                      { value: 'Burns', label: 'Burns' },
                      { value: 'Poisoning', label: 'Poisoning' },
                      { value: 'Suicide Attempt', label: 'Suicide Attempt' },
                      { value: 'Sexual Assault', label: 'Sexual Assault' },
                      { value: 'Fall', label: 'Fall' },
                      { value: 'Other', label: 'Other' },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <div className="oi-mlc-label">Police Station</div>
                  <Input
                    placeholder="Optional"
                    value={mlcStation}
                    onChange={e => setMlcStation(e.target.value)}
                    disabled={readOnly}
                    allowClear
                  />
                </Col>
                <Col span={12}>
                  <div className="oi-mlc-label">FIR Number</div>
                  <Input
                    placeholder="Optional"
                    value={mlcFir}
                    onChange={e => setMlcFir(e.target.value)}
                    disabled={readOnly}
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <div className="oi-mlc-label">Remarks</div>
                  <TextArea
                    rows={2}
                    placeholder="Optional — additional details"
                    value={mlcRemarks}
                    onChange={e => setMlcRemarks(e.target.value)}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>

      {/* ── Item Information ──────────────────────────────────────────────── */}
      <div className="oi-panel">
        <div className="oi-panel-head">
          <MedicineBoxOutlined /> Item Information
        </div>
        <div className="oi-panel-body">
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <div className="oi-grid-label">Billing Code</div>
              <Input
                placeholder="Search billing / item code"
                value={billingCodeSearch}
                onChange={e => setBillingCodeSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col span={12}>
              <div className="oi-grid-label">Description</div>
              <Input
                placeholder="Search item description"
                value={descSearch}
                onChange={e => setDescSearch(e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          {(billingCodeSearch || descSearch) && (
            <div className="oi-item-matches">
              {matchedItems.length === 0 ? (
                <div className="oi-item-empty">No matching item codes</div>
              ) : (
                matchedItems.map(item => (
                  <div className="oi-item-match" key={item.id}>
                    <span className="oi-item-code">{item.his_billing_code || item.item_code}</span>
                    <span className="oi-item-name">{item.item_name}</span>
                    <span className="oi-item-mods">
                      {(item.modalities || []).join(', ') || '—'}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="oi-scans">
            <Table
              columns={scanColumns}
              dataSource={scans?.data}
              pagination={false}
              size="small"
            />
          </div>
        </div>
      </div>

      {/* ── Medical Information ───────────────────────────────────────────── */}
      <div className="oi-panel">
        <div className="oi-panel-head">
          <HeartOutlined /> Medical Information
        </div>
        <div className="oi-panel-body">
          {MEDICAL_QUESTIONS.map(q => (
            <div className="oi-field" key={q.key}>
              <span className="oi-field-label oi-med-label">{q.label}</span>
              <Radio.Group
                options={YES_NO_NA_OPTIONS}
                value={med?.[q.key]}
                onChange={e => setMedValue(q.key, e.target.value)}
                disabled={readOnly}
              />
            </div>
          ))}
          <span className="oi-notes-label">High Risk Message</span>
          <TextArea
            rows={2}
            value={highRiskMsg}
            onChange={e => setHighRiskMsg(e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* ── Referring Physician + Notes ───────────────────────────────────── */}
      <div className="oi-panel">
        <div className="oi-panel-head">
          <UserOutlined /> Referring Physician &amp; Notes
        </div>
        <div className="oi-panel-body">
          <span className="oi-notes-label">Referring Physician</span>
          <Input
            placeholder="Referring physician"
            value={refDoc}
            onChange={e => setRefDoc(e.target.value)}
            disabled={readOnly}
            allowClear
          />

          <span className="oi-notes-label">Notes</span>
          <TextArea
            disabled={readOnly}
            onChange={e => {
              setClinicalNotes(e.target.value);
            }}
          />
        </div>
      </div>

      <div className="oi-actions">
        <Button
          type="primary"
          disabled={readOnly || checkButtonEnable}
          loading={saving || mlcSaving}
          onClick={handleSubmit}
        >
          UPDATE INFO
        </Button>
      </div>
    </div>
  );
}

export default OrderInfoPopup;
